// Licensed to the .NET Foundation under one or more agreements.
// The .NET Foundation licenses this file to you under the MIT license.

import { TemplateResult, html, render } from 'lit-html'
import { classMap } from 'lit-html/directives/class-map.js'
import { breakWordLit, meta, isExternalHref, loc, isSameURL } from './helper'

export type TocNode = {
  name?: string
  href?: string
  expanded?: boolean
  items?: TocNode[]
  shortcut?: boolean
}

/**
 * @returns active TOC nodes
 */
export async function renderToc(): Promise<TocNode[]> {
  const items = await fetchFullToc()
  const tocrel = meta('docfx:tocrel')
  if (!tocrel) {
    hideTocActionBar()
    return []
  }
  const disableTocFilter = meta('docfx:disabletocfilter') === 'true'

  const tocUrl = new URL(tocrel.replace(/.html$/gi, '.json'), window.location.href)
  const pdf = ''
  const pdfFileName = ''
  // const { items, pdf, pdfFileName } = await (await fetch(tocUrl)).json()
  if (items.length <= 0) {
    hideTocActionBar()
    return []
  }

  showTocActionBar()
  const tocFilterUrl = disableTocFilter ? '' : (localStorage?.getItem('tocFilterUrl') || '')
  let tocFilter = disableTocFilter ? '' : (localStorage?.getItem('tocFilter') || '')

  if (tocFilterUrl === '' || tocFilterUrl !== tocUrl.toString()) {
    tocFilter = ''
    if (!disableTocFilter) {
      localStorage?.setItem('tocFilter', '')
      localStorage?.setItem('tocFilterUrl', tocUrl.toString())
    }
  }

  const activeNodes = []
  const selectedNodes = []
  items.forEach(initTocNodes)

  const tocContainer = document.getElementById('toc')
  if (tocContainer) {
    renderToc()

    const activeElements = tocContainer.querySelectorAll('li.active')
    const lastActiveElement = activeElements[activeElements.length - 1]
    if (lastActiveElement) {
      lastActiveElement.scrollIntoView({ block: 'nearest' })
      /* eslint-disable no-self-assign */
      if (location.hash) {
        location.href = location.href
      }
    }
  }

  if (selectedNodes.length > 0) {
    renderNextArticle(items, selectedNodes[0])
  }

  // return activeNodes.slice(0, -1)
  return activeNodes

  function initTocNodes(node: TocNode): boolean {
    let active
    if (node.href) {
      const url = new URL(node.href, tocUrl)
      const isShortCut = node.shortcut ?? false
      active = isExternalHref(url) ? false : isSameURL(url, window.location) && !isShortCut
      if (active) {
        if (node.items) {
          node.expanded = true
        }
        selectedNodes.push(node)
      }
    }

    // Shorten the name of the TabularEditor namespace. - commented out for now, as it may be confusing in some cases.
    // if (node.name) {
    //   node.name = node.name.replace('TabularEditor.', '.')
    // }

    if (node.items) {
      for (const child of node.items) {
        if (initTocNodes(child)) {
          active = true
          node.expanded = true
        }
      }
    }

    if (active) {
      activeNodes.unshift(node)
      return true
    }
    return false
  }

  function renderToc() {
    render(html`
      ${renderTocFilter()}
      <div class="flex-fill overflow-y-auto">${renderTocNodes(items, false) || renderNoFilterResult()}</div>
      ${renderDownloadPdf()}`, tocContainer)
  }

  function renderTocNodes(nodes: TocNode[], parentFilterMatch: boolean): TemplateResult {
    const result = nodes.map(node => {
      const { href, name, items, expanded } = node
      if (!name) {
        return null
      }
      const isLeaf = !items || items.length <= 0
      const filterMatch = (parentFilterMatch || (tocFilter !== '' && name.toLowerCase().includes(tocFilter.toLowerCase())))
      const children = isLeaf ? null : renderTocNodes(items, filterMatch)
      if (tocFilter !== '' && !children && !name.toLowerCase().includes(tocFilter.toLowerCase()) && !filterMatch) {
        return null
      }

      const dom = href
        ? html`<a class='${classMap({ 'nav-link': !activeNodes.includes(node) })}' href=${href}>${breakWordLit(name)}</a>`
        : (isLeaf
            ? html`<span class='text-body-tertiary name-only'>${breakWordLit(name)}</a>`
            : html`<a class='${classMap({ 'nav-link': !activeNodes.includes(node) })}' href='#' @click=${toggleExpand}>${breakWordLit(name)}</a>`)

      const isExpanded = (tocFilter !== '' && expanded !== false && children != null) || expanded === true

      return html`
        <li class=${classMap({ expander: !isLeaf, expanded: isExpanded, active: activeNodes.includes(node) })}>
          ${isLeaf ? null : html`<span class='expand-stub' @click=${toggleExpand}></span>`}
          ${dom}
          ${children}
        </li>`

      function toggleExpand(e) {
        e.preventDefault()
        node.expanded = !isExpanded
        renderToc()
      }
    }).filter(node => node)

    return result.length > 0 ? html`<ul>${result}</ul>` : null
  }

  function renderTocFilter(): TemplateResult {
    return disableTocFilter
      ? null
      : html`
      <form class='filter'>
        <i class='bi bi-filter'></i>
        <input class='form-control' @input=${filterToc} value='${tocFilter}' type='search' placeholder='${loc('tocFilter')}' autocomplete='off' aria-label='${loc('tocFilter')}'>
      </form>`

    function filterToc(e: Event) {
      tocFilter = (<HTMLInputElement>e.target).value.trim()
      localStorage?.setItem('tocFilter', tocFilter)
      renderToc()
    }
  }

  function renderNoFilterResult(): TemplateResult {
    return tocFilter === '' ? null : html`<div class='no-result'>${loc('searchNoResults', { query: tocFilter })}</div>`
  }

  function renderDownloadPdf(): TemplateResult {
    return pdf ? html`<div class="py-2 mb-md-4"><a class="pdf-link" href="${new URL(pdfFileName || 'toc.pdf', tocUrl)}">${loc('downloadPdf')}</a></div>` : null
  }
}

function renderNextArticle(items: TocNode[], node: TocNode) {
  const nextArticle = document.getElementById('nextArticle')
  if (!nextArticle) {
    return
  }

  const tocNodes = flattenTocNodesWithHref(items)
  const i = tocNodes.findIndex(n => n === node)
  const prev = tocNodes[i - 1]
  const next = tocNodes[i + 1]
  if (!prev && !next) {
    return
  }

  const prevButton = prev ? html`<div class="prev"><span><i class='bi bi-chevron-left'></i> ${loc('prevArticle')}</span> <a href="${prev.href}" rel="prev">${breakWordLit(prev.name)}</a></div>` : null
  const nextButton = next ? html`<div class="next"><span>${loc('nextArticle')} <i class='bi bi-chevron-right'></i></span> <a href="${next.href}" rel="next">${breakWordLit(next.name)}</a></div>` : null

  render(html`${prevButton} ${nextButton}`, nextArticle)

  function flattenTocNodesWithHref(items: TocNode[]) {
    const result = []
    for (const item of items) {
      if (item.href) {
        result.push(item)
      }
      if (item.items) {
        result.push(...flattenTocNodesWithHref(item.items))
      }
    }
    return result
  }
}

/**
 * Fetches the full Table of Contents (TOC) for the documentation site.
 *
 * This function retrieves the root TOC and recursively fetches TOC items from child folders.
 * It processes each TOC item to ensure proper linking and structure.
 *
 * @returns {Promise<Array>} A promise that resolves to an array of TOC items.
 */
async function fetchFullToc() {
  const navrel = meta('docfx:navrel')
  if (!navrel) {
    return []
  }
  // Fetch "nav toc" to always get the root TOC
  const navUrl = new URL(navrel.replace(/.html$/gi, '.json'), window.location.href)
  const { items } = await fetch(navUrl).then(res => res.json())
  const docRel = meta('docfx:rel')

  // Go through nav toc item, fetch items also from toc in child folders.
  for (const item of items) {
    // If item doesn't have a tocHref, it isn't a leaf node, no need to fetch further.
    const tocHref = item.tocHref ?? ''
    if (!tocHref) {
      // Make sure href is relative to the current document.
      item.items = []
      item.href = docRel + item.href
      continue
    } else {
      const homepage = item.homepage ?? ''
      if (homepage) {
        const homepageUrl = new URL(docRel + homepage, window.location.href)
        item.href = homepageUrl.href
      } else {
        item.href = ''
      }
    }

    // Fetch toc items from the child folder.
    const tocUrl = new URL(docRel + tocHref.replace(/.html$/gi, '.json'), window.location.href)
    // Get the path of the toc file to resolve relative links.
    const tocPath = tocUrl.pathname.split('/').slice(0, -1).join('/') + '/'
    const { items } = await (await fetch(tocUrl)).json()
    for (const node of items) {
      const nodeHref = node.href ?? node.topicHref ?? ''
      if (nodeHref) {
        const nodeUrl = new URL(tocPath + nodeHref, window.location.href)
        node.href = nodeUrl.href
      }
      if (!node.items) {
        continue
      }
      checkChildNodesRecursively(tocUrl, tocPath, node.items)
    }
    item.items = items
  }
  return items
}

/**
 * Recursively checks and updates the child nodes in a table of contents (TOC) structure.
 *
 * @param tocUrl - The base URL of the TOC.
 * @param tocPath - The path to the TOC.
 * @param childItems - An array of child nodes to be checked and updated.
 *
 * This function iterates through each child node in the provided `childItems` array.
 * If a child node has an `href` property, it resolves the relative link using the
 * provided `tocUrl` and `tocPath`, and updates the `href` property with the resolved URL.
 * Additionally, it checks if the link is a shortcut link by verifying if the resolved
 * `href` does not include the `tocPath`, and sets the `shortcut` property accordingly.
 * If a child node has nested items, the function calls itself recursively to process
 * the nested items.
 */
function checkChildNodesRecursively(tocUrl :URL, tocPath: string, childItems) {
  for (const child of childItems) {
    if (child.href) {
      // Need to resolve relative links.
      child.href = new URL(tocPath + child.href, tocUrl).href
      // Check if the link is a shortcut link.
      child.shortcut = !child.href.includes(tocPath)
    }
    if (child.items) {
      checkChildNodesRecursively(tocUrl, tocPath, child.items)
    }
  }
}

/**
 * Hides the Table of Contents (TOC) action bar by adding the 'visually-hidden' class.
 * If the TOC action bar element is not found or is already hidden, the function does nothing.
 */
function hideTocActionBar() {
  const tocActionbar = document.getElementById('toc-actionbar')
  if (tocActionbar && !tocActionbar.classList.contains('visually-hidden')) {
    tocActionbar.classList.add('visually-hidden')
  }
  const tocToggler = document.getElementById('toc-toggler-header')
  if (tocToggler && !tocToggler.classList.contains('visually-hidden')) {
    tocToggler.classList.add('visually-hidden')
  }
}

/**
 * Displays the Table of Contents (TOC) action bar if it is currently hidden.
 *
 * This function checks for the presence of an element with the ID 'toc-actionbar'.
 * If the element is found and it has the 'visually-hidden' class, the class is removed,
 * making the action bar visible.
 */
function showTocActionBar() {
  const tocActionbar = document.getElementById('toc-actionbar')
  if (tocActionbar && tocActionbar.classList.contains('visually-hidden')) {
    tocActionbar.classList.remove('visually-hidden')
  }
  const tocToggler = document.getElementById('toc-toggler-header')
  if (tocToggler && tocToggler.classList.contains('visually-hidden')) {
    tocToggler.classList.remove('visually-hidden')
  }
}
